/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */

import { Linkable } from '@/components/blocks/Linkable';
import { MuiCardMedia } from '@/components/blocks/MuiCardMedia';
//import { ProductCardAddToCartWithInventory } from '@/components/blocks/ProductCard/parts/AddToCartWithInventory';
import { ProductCardChooseOptions } from '@/components/blocks/ProductCard/parts/ChooseOptions';
import { productCardSX } from '@/components/blocks/ProductCard/styles/card';
import { productCardContentSX } from '@/components/blocks/ProductCard/styles/cardContent';
import { productCardMediaSX, productCardRibbonSX } from '@/components/blocks/ProductCard/styles/cardMedia';
import { productCardDetailsSX } from '@/components/blocks/ProductCard/styles/details';
import { productCardNameSX } from '@/components/blocks/ProductCard/styles/name';
import { Swatch } from '@/components/blocks/Swatch';
import { useCatalogEntryList } from '@/data/Content/CatalogEntryList';
import { useNextRouter } from '@/data/Content/_NextRouter';
import { useProductCard } from '@/data/Content/_ProductCard';
import { useProductEvents } from '@/data/Content/_ProductEvents';
import { useLocalization } from '@/data/Localization';
import { useSettings } from '@/data/Settings';
import { RibbonImage } from '@/data/types/Product';
import { TYPES } from '@/data/constants/product';
import { ContentContext, ContentProvider } from '@/data/context/content';
import { HCLBreadcrumb } from '@/data/types/Breadcrumb';
import { ProductType } from '@/data/types/Product';
import { ProductCardContextValue } from '@/data/types/ProductCard';
import { Box, Card, CardContent, Stack, SxProps, Typography } from '@mui/material';
import { FC, useContext, useMemo } from 'react';
import { useWebPSupportCheck } from "react-use-webp-support-check";
import { Img } from '@/components/blocks/MaterialImage';
import { hideNoChildSX } from '@/styles/common';
import { productCardPriceSX } from './styles/price';
import { combineSX } from '@/utils/combineSX';
import { productIsA } from '@/utils/productIsA';
import { ProductCardPrice } from './Price';

export const ProductCard: FC<{
	product: ProductType;
	parentCrumb?: HCLBreadcrumb[];
	clickAction?: () => void;
	showInventory?: boolean;
	styles?:{[key:string]:SxProps}
}> = ({ product, clickAction, styles, parentCrumb, showInventory }) => {
	const parentCtxValue = useContext(ContentContext) as ReturnType<typeof useCatalogEntryList>;
	const router = useNextRouter();
	//const { trail } = router.query;
	const eventProps = useMemo(() => ({ product }), [product]);
	const priceDisplayNLS = useLocalization('PriceDisplay');
	const productCardValue = useProductCard(product);
	const { onSwatch, sku } = productCardValue;
	const {settings} = useSettings();
	const supportsWebP = useWebPSupportCheck();
	const isWebpON = settings.userData['isWebpON'];
	const { onClick } = useProductEvents(eventProps);
	const routeUrl = useMemo(
		() => product?.seo?.href as string,
		[product]
	);

		 const ribbonData:RibbonImage = product?.ribbonImages?product?.ribbonImages[0]:{ribbonName:'',ribbonPath:''};
	// TODO later for webp
	/* const backupImg = (sku ?? product).thumbnail;
	const onMediaFallback = (event:any) => event.target.src = backupImg;	
	if(supportsWebP && isWebpON && ((sku ?? product).thumbnail.indexOf('webp') === -1)){
		(sku ?? product).thumbnail = (sku ?? product).thumbnail.replace('catalog/thumb','catalog/thumb-webp').replace('.jpg','.webp');
	} */
	
	const contextValue: ProductCardContextValue = useMemo(
		() => ({ ...parentCtxValue, ...productCardValue, product, routeUrl }),
		[parentCtxValue, product, productCardValue, routeUrl]
	);
	const shouldShowInventory =
		product.items || productIsA(product, TYPES.kit) || productIsA(product, TYPES.sku);
	return (
		<Card
			onClick={onClick(clickAction)}
			sx={productCardSX}
			id={product.partNumber}
			data-testid={product.partNumber}
		>
			{/* <CardActions>
				<ProductCardCompareBox product={product} />
			</CardActions> */}
			<Box sx={productCardDetailsSX}>
				<Linkable href={routeUrl} color="textPrimary">
				<Box>
					{ribbonData?.ribbonPath && 
						<Box sx={productCardRibbonSX}>
							<Img
								src={ribbonData.ribbonPath}
								alt={ribbonData.ribbonName}>
							</Img>
						</Box>
					}
					<MuiCardMedia
							sx={combineSX([productCardMediaSX, styles?.cardImageSx?styles.cardImageSx:{}])}
							component="img"
							image={`${settings.userData.StaticHostSecure}${(sku ?? product).thumbnail}`}
							title={product.name}
							alt={product.name}
							height="200"
							width="200"
							/* onError={onMediaFallback} */
					></MuiCardMedia>
				</Box>
			</Linkable>
			<CardContent sx={productCardContentSX}>
				<Stack spacing={1}>
					<Typography
						variant="body2AsH2"
						align="center"
						id={`${product.partNumber}-name`}
						data-testid={`${product.partNumber}-name`}
						sx={combineSX([productCardNameSX, styles?.cardNameSx??styles?.cardNameSx])}
					>
						<Linkable href={product.seo?.href} color="textPrimary">
							{product.name}
						</Linkable>
					</Typography>
					<Stack direction="row" justifyContent="center" spacing={1} sx={hideNoChildSX}>
						{product?.colorSwatches?.map((colorSwatch) => (
							<Swatch
								key={colorSwatch.identifier}
								title={colorSwatch.identifier}
								size="medium"
								image={colorSwatch.image1path}
								data-testid={`product-${colorSwatch.identifier.toLowerCase()}-swatch`}
								id={`product-${colorSwatch.identifier.toLowerCase()}-swatch`}
								onClick={(event) => onSwatch(event, colorSwatch)}
							/>
						))}
					</Stack>
					{product?.productPrice?.min ? (
						<ProductCardPrice price={product.productPrice}/>
					) : (
						<Typography variant="body1" align="center" sx={productCardPriceSX}>
							{priceDisplayNLS.Labels.Pending.t()}
						</Typography>
					)}
				</Stack>
			</CardContent>
			</Box>
			{showInventory ? (
				<ContentProvider value={contextValue}>
					{/* {shouldShowInventory ? (
						<ProductCardAddToCartWithInventory />
					) : (
						<ProductCardChooseOptions />
					)} */}
					<ProductCardChooseOptions />
				</ContentProvider>
			) : null}
		</Card>
	);
};